<script>
    import { onMount } from 'svelte';
    import Cookies from 'js-cookie';
    import merge from 'lodash/merge';

    import { string, array, object, number } from 'yup';

    import { createForm } from 'svelte-forms-lib';
    import { getNotificationsContext } from 'svelte-notifications';
    import FilePond from 'svelte-filepond';
    import SignaturePad from 'signature_pad';

    import { dispatchApi } from 'src/api';
    import { bindClass } from 'src/utils/forms';

    import SignatureSwitch, { MECHANIC, REPAIR_STATION } from './SignatureSwitch.svelte';
    import TextArea from 'src/fields/TextArea.svelte';
    import Number from 'src/fields/Number.svelte';
    import AirportSelect from 'src/fields/AirportSelect.svelte';
    import Text from 'src/fields/Text.svelte';
    import Date from 'src/fields/Date.svelte';
    import Btn from 'src/fields/Button.svelte';

    export let uid;
    let url = `public/discrepancy/${uid}/`;
    let pond;

    const defaultModel = {
        corrective_action: '',
        short_corrective_action: '',
        corrected_state: { },
        replaced_parts: [],
        corrected_by: {
            type: 'third-party',
            owner_type: MECHANIC,
        },
    };

    let discrepancy = defaultModel;
    let pending = true;
    let signaturePad;
    let success = false;

    const { addNotification } = getNotificationsContext();
    const {
        form,
        state,
        handleSubmit,
        updateField,
        updateValidateField,
        updateInitialValues,
    } = createForm({
        initialValues: merge({ }, defaultModel),
        validationSchema: object({
            corrective_action: string().required(),
            corrected_state: object({
                airframe_hours: number().min(0).required(),
                airframe_landings: number().min(0).integer().required(),
                location_iata: string().required(),
            }),
            corrected_by: object({
                owner_type: string().oneOf([MECHANIC, REPAIR_STATION]),
                type: string().oneOf(['third-party']),
                date: string().required(),
                holder_name: string().required(),
                sign_data: string().required(),
                cert_type_and_number: string().when('owner_type', {
                  is: MECHANIC,
                  then: schema => schema.required(),
                }),
                repair_station: string().when('owner_type', {
                    is: REPAIR_STATION,
                    then: schema => schema.required(),
                }),
            }),
            replaced_parts: array()
                .of(
                    object({
                        removed: object({
                            part_number: string().required(),
                            serial_number: string().required(),
                        }),
                        installed: object({
                            part_number: string().required(),
                            serial_number: string().required(),
                        }),
                    })
                )
                .max(3),
        }),
        async onSubmit(values) {
            try {
                const { data } = await dispatchApi.patch(url, values);
                discrepancy = merge(data, defaultModel);
                updateInitialValues(discrepancy);
                success = true;
            } catch (e) {
                addNotification({
                    position: 'top-right',
                    type: 'danger',
                    text: 'An error occurred while submitting the form. Please contact ENG team: eng@boutiqueair.com.',
                    removeAfter: 6000,
                });
                console.error(e);
            }
        }
    })

    onMount(() => fetchDiscrepancy());

    const fetchDiscrepancy = async () => {
        pending = true;
        try {
            const { data } = await dispatchApi.get(url);
            discrepancy = merge(data, defaultModel);
            updateInitialValues(discrepancy);
        } finally {
            pending = false;
        }
    };

    const fillCurrentAircraftValues = async () => {
        const {
            aftt,
            airframe_landings,
            location,
            repair_location,
        } = discrepancy.aircraft;

        const correctedState  = {
            airframe_hours: aftt,
            location_iata: repair_location || location,
            airframe_landings,
        };
        updateValidateField('corrected_state', correctedState);
    };

    const addNewPart = async () => {
        const newParts = {
            removed: { part_number: '', serial_number: '' },
            installed: { part_number: '', serial_number: '' },
        };
        updateField('replaced_parts', [...$form.replaced_parts, newParts]);
    };

    const removeReplacedPart = (ix) => () => {
        const parts = $form.replaced_parts;
        const updatedParts = [
            ...parts.slice(0, ix),
            ...parts.slice(ix + 1),
        ];
        updateField('replaced_parts', updatedParts);
    }

    const signature = (canvas) => {
        signaturePad = new SignaturePad(canvas, {
            onEnd() {
                const signData = signaturePad.toDataURL("image/png");
                updateValidateField(['corrected_by', 'sign_data'], signData)
            },
        });
        return {
            destroy() {
                signaturePad = undefined;
            },
        };
    };

    const clearCanvas = () => {
        if (signaturePad) signaturePad.clear();
        updateValidateField(['corrected_by', 'sign_data'], undefined);
        updateValidateField(['corrected_by', 'holder_name'], '');
    };

    const inputId = (...args) => args.join('__');

    $: fileServerConfig = discrepancy.id && {
        url: `/dispatch/api/discrepancy/${discrepancy.id}/files/`,
        process: {
            ondata(formData) {
                formData.append("discrepancy", discrepancy.id);
                formData.append("csrfmiddlewaretoken", Cookies.get("csrftoken"));
                return formData;
            },
            onerror(response) {
                addNotification({
                    position: 'top-right',
                    type: 'danger',
                    text: 'An error has occurred while uploading the document. Please contact ENG team: eng@boutiqueair.com.',
                    removeAfter: 6000,
                });
                console.error(response);
            },
        },
    };

    const handleChange = name => ({ detail }) => updateValidateField(name, detail);

    $: hasConfirmedSignature = !!$form.corrected_by.timestamp;
    $: isSignOnCanvas = !!$form.corrected_by.sign_data;
    $: isMechanic = $form.corrected_by.owner_type === MECHANIC;
</script>

<svelte:head>
    {#if discrepancy.id}
        <title>Discrepancy #{discrepancy.control_number} &mdash; Boutique Air</title>
    {:else}
        <title>Complete Discrepancy &mdash; Boutique Air</title>
    {/if}
</svelte:head>

<main>
    <section class="logo-section">
        <img src="/static/img/boutique_air_logo_linear.svg" alt="Boutique Air" />
    </section>

    {#if success || hasConfirmedSignature}
        <section class="centered">
            <h1>Thank you!</h1>
            <h3>
                Your correction has been successfully recorded.
            </h3>
        </section>
    {:else if discrepancy.id}
        <div class="header">
            <h1 class="title">Complete the Discrepancy</h1>
            <span class="control-number">#{discrepancy.control_number}</span>
        </div>

        <div class="discrepancy-desc">
            <strong>You're about to resolve {discrepancy.aircraft.registration} discrepancy.</strong>

            <div>
                {#if discrepancy.deferrable}
                    <strong>Deferrable. MEL item {discrepancy.ref.code}</strong>
                    ({discrepancy.ref.description}).
                {:else}
                    <strong>Grounding.</strong>
                {/if}
            </div>
            <div>{discrepancy.description}</div>
        </div>

        <section use:bindClass={{ state, name: 'corrective_action' }}>
            <label class="label required" for="corrective-action">Corrective Action</label>
            <TextArea
                id="corrective-action"
                value={$form.corrective_action}
                on:change={handleChange('corrective_action')}
                maxLength={1300}
                fixedWidth
            />
        </section>

        <section>
            <label class="label" for="short-corrective-action">
                Short Corrective Action / BA Form 102a Note
            </label>
            <TextArea
                id="short-corrective-action"
                bind:value={$form.short_corrective_action}
                maxLength={300}
                fixedWidth
            />
        </section>

        <section>
            <h3>
                Aircraft State &nbsp;
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a class="link" href="#" on:click={fillCurrentAircraftValues}>
                    Fill Current Values
                </a>
            </h3>

            <div style="display: flex;">
                <div class="field" use:bindClass={{ state, name: ['corrected_state', 'airframe_hours'] }}>
                    <label class="label required" for="airframe-hours">Airframe Hours</label>
                    <Number
                        id="airframe-hours"
                        value={$form.corrected_state.airframe_hours}
                        on:change={handleChange(['corrected_state', 'airframe_hours'])}
                    />
                </div>
                <div class="field" use:bindClass={{ state, name: ['corrected_state', 'airframe_landings'] }}>
                    <label class="label required" for="airframe-landings">
                        Landings
                    </label>
                    <Number
                        id="airframe-landings"
                        value={$form.corrected_state.airframe_landings}
                        on:change={handleChange(['corrected_state', 'airframe_landings'])}
                    />
                </div>
                <div class="field" style="flex: 1 1 auto;" use:bindClass={{ state, name: ['corrected_state', 'location_iata'] }}>
                    <label class="label required" for="repair-location">Location</label>
                    <AirportSelect
                        id="repair-location"
                        value={$form.corrected_state.location_iata}
                        on:change={handleChange(['corrected_state', 'location_iata'])}
                    />
                </div>
            </div>
        </section>

        <section>
            <h3>Replaced Parts</h3>
            <table class="replaced-parts">
                {#each $form.replaced_parts as part, i}
                    <tr class="replaced-part">
                        <td class="replaced-part__part">
                            <div class="replaced-part__name">Removed</div>
                            <div use:bindClass={{ state, name: ['replaced_parts', i, 'removed', 'part_number'] }}>
                                <label class="label" for={inputId("part-removed", i)}>Part #</label>
                                <Text
                                    id={inputId("part-removed", i)}
                                    value={part.removed.part_number}
                                    on:change={handleChange(['replaced_parts', i, 'removed', 'part_number'])}
                                />
                            </div>
                            <div use:bindClass={{ state, name: ['replaced_parts', i, 'removed', 'serial_number'] }}>
                                <label class="label" for={inputId("serial-removed", i)}>Serial #</label>
                                <Text
                                    id={inputId("serial-removed", i)}
                                    value={part.removed.serial_number}
                                    on:change={handleChange(['replaced_parts', i, 'removed', 'serial_number'])}
                                />
                            </div>
                        </td>

                        <td class="replaced-part__part replaced-part__part_installed">
                            <div class="replaced-part__name">Installed</div>
                            <div use:bindClass={{ state, name: ['replaced_parts', i, 'installed', 'part_number'] }}>
                                <label class="label" for={inputId("part-installed", i)}>Part #</label>
                                <Text
                                    id={inputId("part-installed", i)}
                                    value={part.installed.part_number}
                                    on:change={handleChange(['replaced_parts', i, 'installed', 'part_number'])}
                                />
                            </div>
                            <div use:bindClass={{ state, name: ['replaced_parts', i, 'installed', 'serial_number'] }}>
                                <label class="label" for={inputId("serial-installed", i)}>Serial #</label>
                                <Text
                                    id={inputId("serial-installed", i)}
                                    value={part.installed.serial_number}
                                    on:change={handleChange(['replaced_parts', i, 'installed', 'serial_number'])}
                                />
                            </div>
                            <Btn type="danger"
                                 size="mini"
                                 roundType="round"
                                 class="replaced-part__remove-btn"
                                 on:click={removeReplacedPart(i)}>
                                ×
                            </Btn>
                        </td>
                    </tr>
                {/each}
            </table>

            {#if $form.replaced_parts.length < 3}
                <Btn on:click={addNewPart}>+ Add a New</Btn>
            {/if}
        </section>

        {#if fileServerConfig}
            <section>
                <h3>Attachments</h3>
                <FilePond bind:this={pond} server={fileServerConfig} allowMultiple={true} name="file" />
            </section>
        {/if}

        <section class="corrected-by">
            <div class="corrected-by__header">
                <h3>Corrected By</h3>
                <SignatureSwitch
                    bind:type={$form.corrected_by.owner_type}
                    on:change={handleChange(['corrected_by', 'owner_type'])}
                />
            </div>

            {#if isMechanic}
                <div use:bindClass={{ state, name: ['corrected_by', 'cert_type_and_number'] }}>
                    <label class="label required" for="cert-type-and-number">Cert Type & #</label>
                    <Text
                        id="cert-type-and-number"
                        placeholder="XXX 1234XXXXXX"
                        value={$form.corrected_by.cert_type_and_number}
                        on:change={handleChange(['corrected_by', 'cert_type_and_number'])}
                    />
                </div>
            {:else}
                <div use:bindClass={{ state, name: ['corrected_by', 'repair_station'] }}>
                    <label class="label required" for="repair-station">Repair Station, CRS#</label>
                    <Text
                        id="repair-station"
                        value={$form.corrected_by.repair_station}
                        on:change={handleChange(['corrected_by', 'repair_station'])}
                    />
                </div>
            {/if}

            <div use:bindClass={{ state, name: ['corrected_by', 'date'] }}>
                <label class="label required" for="signature-date">Date</label>
                <Date
                    id="signature-date"
                    value={$form.corrected_by.date}
                    on:change={handleChange(['corrected_by', 'date'])}
                />
            </div>

            <div use:bindClass={{ state, name: ['corrected_by', 'sign_data'] }} style="padding-top: 20px;">
                <label class="label required" for="discrepancy-signature-canvas">
                    Please sign here in the field
                </label>
                <canvas width="320" height="150" id="discrepancy-signature-canvas" use:signature />
            </div>

            <div use:bindClass={{ state, name: ['corrected_by', 'holder_name'] }}
                 style="width: 320px; margin-bottom: 10px;">
                <Text
                    placeholder="John Doe"
                    value={$form.corrected_by.holder_name}
                    on:change={handleChange(['corrected_by', 'holder_name'])}
                />
            </div>

            {#if isSignOnCanvas}
                <Btn on:click={clearCanvas} size="small" type="danger">Clear</Btn>
            {/if}
        </section>

        <section class="footer">
            <Btn on:click={handleSubmit} disabled={$state.isSubmitting}>
                Confirm the Correction
            </Btn>
        </section>
    {:else if !pending}
        <section class="centered">
            <h1>Not Found</h1>
            <h3>Discrepancy had been corrected or does not exist.</h3>
        </section>
    {:else}
        <section class="centered">
            <p>Loading, please wait...</p>
        </section>
    {/if}
</main>

<style>
    main {
        font-size: 14px;
        line-height: 20px;
    }

    main::after {
        content: "";
        display: table;
        clear: both;
    }

    section {
        margin-bottom: 20px;
    }

    .logo-section {
        display: flex;
        flex-flow: column;
        justify-content: center;
        margin-bottom: 20px;
    }

    .logo-section::after {
        content: "";
        display: block;
        border-bottom: 1px solid #ccc;
        width: 280px;
        margin: 0 auto;
    }

    .header {
        display: flex;
        flex-flow: row nowrap;
        align-items: baseline;
    }

    .logo-section img {
        width: 360px;
        margin: 15px auto 10px auto;
    }

    .corrected-by {
        background: rgba(91, 192, 222, 0.15);
        padding: 10px;
    }

    .footer {
        display: flex;
        justify-content: flex-end;
    }

    .centered {
        text-align: center;
    }

    h3 {
        margin: 0 0 10px;
    }

    .discrepancy-desc {
        background: rgba(237, 85, 101, 0.2);
        color: #ed5565;
        padding: 10px 15px;
        margin-bottom: 20px;
    }

    .control-number {
        margin-left: auto;
    }

    .label {
        font-weight: bold;
        line-height: 1.6;
    }

    .field + .field {
        margin-left: 10px;
    }

    .invalid .airport-select {
        --border: 2px solid #ed5565;
    }

    canvas {
        background: #fff;
        margin: 5px 0;
    }

    .replaced-parts {
        width: 100%;
        border-collapse: separate;
    }

    .replaced-part__part {
        margin-right: 5px;
        padding: 10px 10px 0 0;
        position: relative;
    }

    .replaced-part__part_installed {
        background: #f1f1f1;
        padding: 10px 10px 0 10px;
    }

    .replaced-part :global(.replaced-part__remove-btn) {
        position: absolute;
        top: 5px;
        right: 5px;
        display: none;
        padding: 0 5px;
    }

    .replaced-part:hover :global(.replaced-part__remove-btn) {
        display: block;
    }

    .corrected-by__header {
        display: flex;
        column-gap: 20px;
        margin-bottom: 10px;
        align-items: center;
    }

    .corrected-by__header h3 {
        margin: 0;
    }
</style>
